import Link from 'next/link';
import { ContentImage } from '@loomstateorg/shared-frontend/components/commercetools-ui/types';

interface ImageGalleryProps {
  children?: React.ReactNode;

  images: ContentImage[];
  layout?: 'inline' | 'grid';
  className?: string;
}

const ImageGallery: React.FC<ImageGalleryProps> = ({ images, layout = 'inline', className = '' }) => {
  const gridCols = images.length < 3 ? `grid-cols-${images.length}` : 'md:grid-cols-2 lg:grid-cols-3';

  const imageContainerClass = `${
    layout === 'grid' ? `grid ${gridCols}` : 'flex flex-col items-center justify-center md:flex-row'
  }`;

  return images.length ? (
    <div className={`${imageContainerClass} gap-8 ${className}`.trim()} data-testid="atom-image-gallery">
      {images.map((image, index) => (
        <MediaItem
          key={index}
          image={image}
          index={index}
          className={`${images.length === 1 || layout === 'grid' ? 'w-full' : null}`}
        />
      ))}
    </div>
  ) : null;
};

const MediaItem = ({ image, index, className }: { image: ContentImage; index: number; className?: string }) => {
  const mediaClassName = `mx-auto h-auto max-w-full object-contain ${image.imageWidth || 'w-full 2xl:w-full max-2xl:w-auto'}`;

  if (image.videoUrl) {
    return (
      <div className={className} data-testid={`atom-image-gallery-item-${index}`}>
        <video
          data-component-group="image"
          className={mediaClassName}
          autoPlay
          muted
          loop
          playsInline
          preload="none"
          aria-label={image.alt}
        >
          <source src={image.videoUrl} type={image.videoMimeType ?? 'video/mp4'} />
        </video>
      </div>
    );
  }

  const img = (
    <img
      data-component-group="image"
      src={image.src}
      alt={image.alt}
      className={mediaClassName}
    />
  );

  if (image.link) {
    return (
      <Link
        href={image.link}
        className={className}
        target={image.link.startsWith('http') ? '_blank' : undefined}
        rel={image.link.startsWith('http') ? 'noopener noreferrer' : undefined}
        data-testid={`atom-image-gallery-item-${index}`}
      >
        {img}
      </Link>
    );
  }

  return (
    <div className={className} data-testid={`atom-image-gallery-item-${index}`}>
      {img}
    </div>
  );
};

export default ImageGallery;
